import { toast } from '@/hooks/use-toast';
import { REPORT_STATUS_KEYS } from '@/pages/Dashboard/Partials/HourlySalesChart';
import { ReportsService } from '@/services/reports.service';
import { SellsService } from '@/services/sells.service';
import useCurrencyStore from '@/store/useCurrencyStore';
import i18n from '@/translations';
import { hasActiveFiltersBeyondDates } from '@/utils/convertFilters';
import {
  formatDateForAPI,
  getDefaultDate,
  isValidDate,
} from '@/utils/dateHelpers';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { differenceInDays, format, isSameDay, isToday } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsIsoDateTime,
  parseAsString,
  useQueryState,
} from 'nuqs';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';

export interface FilterParams {
  [key: string]: string | string[] | undefined;
  from?: string;
  to?: string;
  q?: string;
}

export type SalesDateAxis = 'processing' | 'paid' | 'refunded';

export interface FilterBadgeLabel {
  [key: string]: string;
}

export interface KeyLabel {
  [key: string]: string;
}

interface ReportsAndPaidSalesData {
  reports: any[];
  paidSales: any;
}

const MULTI_VALUED_KEYS = [
  'payment_methods',
  'status',
  'src',
  'utm_campaign',
  'utm_source',
  'utm_medium',
  'utm_content',
  'utm_term',
  'offers',
  'products',
  'affiliates',
  'coproducers',
  'templates',
] as const;

export const PAYMENT_STATUS = [
  'paid',
  'awaiting_payment',
  'not_authorized',
  'chargeback',
  'refunded',
] as const;

/**
 * Status válidos por eixo de data.
 * - processing (processed_at): todos os pedidos passam → todos os status
 * - paid (paid_at): só pedidos pagos → paid, chargeback, refunded
 * - refunded (refunded_at): só reembolsados → refunded
 */
export const STATUSES_BY_DATE_AXIS: Record<SalesDateAxis, readonly string[]> = {
  processing: PAYMENT_STATUS,
  paid: ['paid', 'chargeback', 'refunded'],
  refunded: ['refunded', 'chargeback'],
} as const;

/**
 * Status pré-selecionado por padrão ao entrar na tela ou trocar de eixo.
 * - processing: só 'paid' (primeira pesquisa mostra pedidos pagos)
 * - paid: só 'paid'
 * - refunded: só 'refunded'
 */
const DEFAULT_STATUS_FOR_AXIS: Record<SalesDateAxis, string[]> = {
  processing: ['paid'],
  paid: ['paid'],
  refunded: ['refunded', 'chargeback'],
};

export const getDefaultStatusForAxis = (axis: SalesDateAxis): string[] => {
  return [...DEFAULT_STATUS_FOR_AXIS[axis]];
};

const STRING_KEYS = [
  'src',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

const SIMPLE_ARRAY_KEYS = ['payment_methods', 'status'] as const;

const QUERY_CONFIG = {
  refetchOnWindowFocus: false,
} as const;

const getLocale = () => (i18n.language === 'pt' ? ptBR : undefined);

export const normalizeParams = (params: FilterParams): FilterParams => {
  const normalized: FilterParams = { ...params };

  MULTI_VALUED_KEYS.forEach((key) => {
    if (STRING_KEYS.includes(key as any)) {
      if (normalized[key] === '') {
        delete normalized[key];
      }
      return;
    }

    if (normalized[key] && !Array.isArray(normalized[key])) {
      if (normalized[key] !== '') {
        normalized[key] = [normalized[key] as string];
      } else {
        delete normalized[key];
      }
    }
  });

  return normalized;
};

export const convertToVisualParams = (
  params: FilterParams,
): Record<string, { label?: string; value?: string }[] | string[] | string> => {
  const result: Record<
    string,
    { label?: string; value?: string }[] | string[] | string
  > = {};

  Object.entries(params).forEach(([key, value]) => {
    if (['from', 'to', 'q'].includes(key)) return;
    if (typeof value === 'string' && value === '') return;

    if (STRING_KEYS.includes(key as any)) {
      if (typeof value === 'string') {
        result[key] = value;
      } else if (Array.isArray(value) && value.length > 0) {
        result[key] = value[0];
      }
      return;
    }

    if (SIMPLE_ARRAY_KEYS.includes(key as any) && Array.isArray(value)) {
      const nonEmptyValues = value.filter((v) => v !== '');
      if (nonEmptyValues.length > 0) {
        result[key] = nonEmptyValues;
      }
      return;
    }

    if (Array.isArray(value)) {
      const nonEmptyValues = value.filter((v) => v !== '');
      if (nonEmptyValues.length > 0) {
        result[key] = nonEmptyValues.map((item) => {
          if (typeof item === 'string' && item.includes(':::')) {
            const [label, val] = item.split(':::');
            return { label, value: val };
          }
          return { label: item, value: item };
        });
      }
      return;
    }

    if (typeof value === 'string') {
      result[key] = value;
    }
  });

  return result;
};

export const convertToSellsFilterParams = (
  params: FilterParams,
  dateAxis?: SalesDateAxis,
): any => {
  const result: any = {};

  Object.entries(params).forEach(([key, value]) => {
    if (['from', 'to', 'q'].includes(key)) return;

    if (STRING_KEYS.includes(key as any)) {
      if (typeof value === 'string' && value !== '') {
        result[key] = value.includes(':::') ? value.split(':::')[1] : value;
      }
      return;
    }

    if (MULTI_VALUED_KEYS.includes(key as any)) {
      const values = Array.isArray(value) ? value : [value];
      const filteredValues = values
        .map((v) =>
          typeof v === 'string' && v.includes(':::') ? v.split(':::')[1] : v,
        )
        .filter((v) => v !== '');

      if (filteredValues.length > 0) {
        result[key] = filteredValues;
      }
    } else if (typeof value === 'string' && value !== '') {
      result[key] = value.includes(':::') ? value.split(':::')[1] : value;
    }
  });

  return result;
};

export const useSales = () => {
  const { t } = useTranslation(['sales', 'common']);

  const [currentPage, setCurrentPage] = useQueryState(
    'page',
    parseAsInteger.withDefault(1),
  );
  const [searchQuery, setSearchQuery] = useQueryState('q', parseAsString);

  const [fromDate, setFromDate] = useQueryState(
    'from',
    parseAsIsoDateTime.withDefault(getDefaultDate()),
  );
  const [toDate, setToDate] = useQueryState(
    'to',
    parseAsIsoDateTime.withDefault(getDefaultDate()),
  );

  const [dateAxis, setDateAxis] = useQueryState(
    'date_axis',
    parseAsString.withDefault('processing'),
  );

  const [statusFilter, setStatusFilter] = useQueryState(
    'status',
    parseAsArrayOf(parseAsString),
  );
  const [paymentMethodsFilter, setPaymentMethodsFilter] = useQueryState(
    'payment_methods',
    parseAsArrayOf(parseAsString),
  );
  const [srcFilter, setSrcFilter] = useQueryState('src', parseAsString);
  const [utmCampaignFilter, setUtmCampaignFilter] = useQueryState(
    'utm_campaign',
    parseAsString,
  );
  const [utmSourceFilter, setUtmSourceFilter] = useQueryState(
    'utm_source',
    parseAsString,
  );
  const [utmMediumFilter, setUtmMediumFilter] = useQueryState(
    'utm_medium',
    parseAsString,
  );
  const [utmContentFilter, setUtmContentFilter] = useQueryState(
    'utm_content',
    parseAsString,
  );
  const [utmTermFilter, setUtmTermFilter] = useQueryState(
    'utm_term',
    parseAsString,
  );
  const [offersFilter, setOffersFilter] = useQueryState(
    'offers',
    parseAsArrayOf(parseAsString),
  );
  const [productsFilter, setProductsFilter] = useQueryState(
    'products',
    parseAsArrayOf(parseAsString),
  );
  const [affiliatesFilter, setAffiliatesFilter] = useQueryState(
    'affiliates',
    parseAsArrayOf(parseAsString),
  );
  const [coproducersFilter, setCoproducersFilter] = useQueryState(
    'coproducers',
    parseAsArrayOf(parseAsString),
  );
  const [templatesFilter, setTemplatesFilter] = useQueryState(
    'templates',
    parseAsArrayOf(parseAsString),
  );

  const currency = useCurrencyStore((s) => s.currency);
  const prevCurrencyRef = useRef(currency);

  useEffect(() => {
    if (prevCurrencyRef.current === currency) return;
    prevCurrencyRef.current = currency;
    setPaymentMethodsFilter(null);
    setCurrentPage(1);
  }, [currency, setPaymentMethodsFilter, setCurrentPage]);

  const [debouncedQuery] = useDebounce(searchQuery, 650);

  const dateRange = useMemo(
    (): DateRange => ({
      from: fromDate,
      to: toDate,
    }),
    [fromDate, toDate],
  );

  const effectiveStatusFilter = useMemo(() => {
    if (statusFilter?.length) return statusFilter;
    return getDefaultStatusForAxis((dateAxis as SalesDateAxis) || 'processing');
  }, [statusFilter, dateAxis]);

  const filterParams = useMemo((): FilterParams => {
    const params: FilterParams = {};

    if (effectiveStatusFilter?.length) params.status = effectiveStatusFilter;
    if (paymentMethodsFilter?.length)
      params.payment_methods = paymentMethodsFilter;
    if (srcFilter) params.src = srcFilter;
    if (utmCampaignFilter) params.utm_campaign = utmCampaignFilter;
    if (utmSourceFilter) params.utm_source = utmSourceFilter;
    if (utmMediumFilter) params.utm_medium = utmMediumFilter;
    if (utmContentFilter) params.utm_content = utmContentFilter;
    if (utmTermFilter) params.utm_term = utmTermFilter;
    if (offersFilter?.length) params.offers = offersFilter;
    if (productsFilter?.length) params.products = productsFilter;
    if (affiliatesFilter?.length) params.affiliates = affiliatesFilter;
    if (coproducersFilter?.length) params.coproducers = coproducersFilter;
    if (templatesFilter?.length) params.templates = templatesFilter;

    return normalizeParams(params);
  }, [
    statusFilter,
    paymentMethodsFilter,
    srcFilter,
    utmCampaignFilter,
    utmSourceFilter,
    utmMediumFilter,
    utmContentFilter,
    utmTermFilter,
    offersFilter,
    productsFilter,
    affiliatesFilter,
    coproducersFilter,
    templatesFilter,
    currency,
  ]);

  const filterVisualParams = useMemo(
    () => convertToVisualParams(filterParams),
    [filterParams],
  );

  const mapReportsToChartData = useCallback((rawData: any[] | undefined) => {
    if (!rawData?.length) return [];
    const first = rawData[0] as Record<string, unknown>;
    const hasMultiStatus = REPORT_STATUS_KEYS.some(
      (key) =>
        key in first &&
        (typeof first[key] === 'number' || typeof first[key] === 'string'),
    );
    if (hasMultiStatus) {
      return rawData.map((report: Record<string, unknown>) => {
        const point: Record<string, unknown> = {
          hour: report.date_formatted ?? report.date,
        };
        REPORT_STATUS_KEYS.forEach((key) => {
          if (
            key in report &&
            (typeof report[key] === 'number' || typeof report[key] === 'string')
          ) {
            point[key] = report[key];
          }
          const formattedKey = `${key}_formatted`;
          if (formattedKey in report && report[formattedKey] != null) {
            point[formattedKey] = report[formattedKey];
          }
        });
        return point;
      });
    }
    return rawData.map((report: any) => ({
      hour: report.date_formatted,
      tooltipLabel: report.total_amount_formatted,
      value: report.total_amount,
    }));
  }, []);

  const fetchReports = useCallback(async () => {
    const fromDate = dateRange.from || getDefaultDate();
    const toDate = dateRange.to || getDefaultDate();

    const reportsFilterParams = convertToSellsFilterParams(
      filterParams,
      dateAxis as SalesDateAxis,
    );

    if (!isValidDate(fromDate) || !isValidDate(toDate)) {
      const defaultDate = getDefaultDate();
      const data = await ReportsService.getReports({
        startDate: formatDateForAPI(defaultDate),
        endDate: formatDateForAPI(defaultDate),
        dateAxis: dateAxis as SalesDateAxis,
        filterParams: reportsFilterParams,
        q: debouncedQuery ?? undefined,
        currency,
      });
      return (
        data?.data?.map((report: any) => ({
          hour: report.date_formatted,
          paid: report.paid,
          paid_formatted: report.paid_formatted,
          awaiting_payment: report.awaiting_payment,
          awaiting_payment_formatted: report.awaiting_payment_formatted,
          not_authorized: report.not_authorized,
          not_authorized_formatted: report.not_authorized_formatted,
          chargeback: report.chargeback,
          chargeback_formatted: report.chargeback_formatted,
          refunded: report.refunded,
          refunded_formatted: report.refunded_formatted,
        })) || []
      );
    }

    const data = await ReportsService.getReports({
      startDate: formatDateForAPI(fromDate),
      endDate: formatDateForAPI(toDate),
      dateAxis: dateAxis as SalesDateAxis,
      filterParams: reportsFilterParams,
      q: debouncedQuery ?? undefined,
      currency,
    });

    return (
      data?.data?.map((report: any) => ({
        hour: report.date_formatted,
        paid: report.paid,
        paid_formatted: report.paid_formatted,
        awaiting_payment: report.awaiting_payment,
        awaiting_payment_formatted: report.awaiting_payment_formatted,
        not_authorized: report.not_authorized,
        not_authorized_formatted: report.not_authorized_formatted,
        chargeback: report.chargeback,
        chargeback_formatted: report.chargeback_formatted,
        refunded: report.refunded,
        refunded_formatted: report.refunded_formatted,
      })) || []
    );
  }, [
    dateRange.from,
    dateRange.to,
    filterParams,
    debouncedQuery,
    currency,
    dateAxis,
  ]);

  const fetchPaidSales = useCallback(async () => {
    const fromDate = dateRange.from || getDefaultDate();
    const toDate = dateRange.to || getDefaultDate();

    const filterParamsConverted = convertToSellsFilterParams(
      filterParams,
      dateAxis as SalesDateAxis,
    );

    if (!isValidDate(fromDate) || !isValidDate(toDate)) {
      const defaultDate = getDefaultDate();

      return ReportsService.getReportsSalesByStatus({
        startDate: formatDateForAPI(defaultDate),
        endDate: formatDateForAPI(defaultDate),
        dateAxis: dateAxis as SalesDateAxis,
        filterParams: filterParamsConverted,
        q: debouncedQuery ?? undefined,
        currency,
      });
    }

    return ReportsService.getReportsSalesByStatus({
      startDate: formatDateForAPI(fromDate),
      endDate: formatDateForAPI(toDate),
      dateAxis: dateAxis as SalesDateAxis,
      filterParams: filterParamsConverted,
      q: debouncedQuery ?? undefined,
      currency,
    });
  }, [
    dateRange.from,
    dateRange.to,
    filterParams,
    debouncedQuery,
    currency,
    dateAxis,
  ]);

  const fetchSales = useCallback(async () => {
    const fromDate = dateRange.from || getDefaultDate();
    const toDate = dateRange.to || getDefaultDate();

    const sellsFilterParams = convertToSellsFilterParams(
      filterParams,
      dateAxis as SalesDateAxis,
    );

    if (!isValidDate(fromDate) || !isValidDate(toDate)) {
      const defaultDate = getDefaultDate();
      return SellsService.getSells({
        page: currentPage,
        startDate: formatDateForAPI(defaultDate),
        endDate: formatDateForAPI(defaultDate),
        dateAxis: dateAxis as SalesDateAxis,
        filterParams: sellsFilterParams,
        q: debouncedQuery ?? undefined,
        currency,
      });
    }

    return SellsService.getSells({
      page: currentPage,
      startDate: formatDateForAPI(fromDate),
      endDate: formatDateForAPI(toDate),
      dateAxis: dateAxis as SalesDateAxis,
      filterParams: sellsFilterParams,
      q: debouncedQuery ?? undefined,
      currency,
    });
  }, [
    currentPage,
    dateRange.from,
    dateRange.to,
    filterParams,
    debouncedQuery,
    currency,
    dateAxis,
  ]);

  const fetchReportsAndPaidSales =
    useCallback(async (): Promise<ReportsAndPaidSalesData> => {
      try {
        const [reportsData, paidSalesData] = await Promise.allSettled([
          fetchReports(),
          fetchPaidSales(),
        ]);

        return {
          reports: reportsData.status === 'fulfilled' ? reportsData.value : [],
          paidSales:
            paidSalesData.status === 'fulfilled' ? paidSalesData.value : null,
        };
      } catch (error) {
        console.error('Error fetching reports and paid sales:', error);
        throw error;
      }
    }, [fetchReports, fetchPaidSales]);

  const reportsQueryKey = useMemo(() => {
    const fromDate = dateRange.from || getDefaultDate();
    const toDate = dateRange.to || getDefaultDate();

    return [
      'reports-and-paid-sales',
      isValidDate(fromDate)
        ? formatDateForAPI(fromDate)
        : formatDateForAPI(getDefaultDate()),
      isValidDate(toDate)
        ? formatDateForAPI(toDate)
        : formatDateForAPI(getDefaultDate()),
      dateAxis,
      JSON.stringify(filterParams),
      debouncedQuery || null,
      currency,
    ];
  }, [
    dateRange.from,
    dateRange.to,
    filterParams,
    debouncedQuery,
    currency,
    dateAxis,
  ]);

  const salesQueryKey = useMemo(() => {
    const fromDate = dateRange.from || getDefaultDate();
    const toDate = dateRange.to || getDefaultDate();

    return [
      'sales',
      currentPage,
      isValidDate(fromDate)
        ? formatDateForAPI(fromDate)
        : formatDateForAPI(getDefaultDate()),
      isValidDate(toDate)
        ? formatDateForAPI(toDate)
        : formatDateForAPI(getDefaultDate()),
      dateAxis,
      JSON.stringify(filterParams),
      debouncedQuery || null,
      currency,
    ];
  }, [
    currentPage,
    dateRange.from,
    dateRange.to,
    filterParams,
    debouncedQuery,
    currency,
    dateAxis,
  ]);

  const {
    data: reportsAndPaidSalesData,
    isPending: isLoadingReportsAndPaidSales,
    isRefetching: isFetchingReportsAndPaidSales,
    error: reportsError,
  } = useQuery({
    queryKey: reportsQueryKey,
    queryFn: fetchReportsAndPaidSales,
    ...QUERY_CONFIG,
  });

  const {
    data: salesData,
    isPending: isLoadingSales,
    isRefetching: isFetchingSales,
    error: salesError,
  } = useQuery({
    queryKey: salesQueryKey,
    queryFn: fetchSales,
    ...QUERY_CONFIG,
  });

  const handleExportFile = useCallback(async () => {
    const fromDate = dateRange.from || getDefaultDate();
    const toDate = dateRange.to || getDefaultDate();

    if (!isValidDate(fromDate) || !isValidDate(toDate)) {
      const defaultDate = getDefaultDate();
      return SellsService.downloadExportSalesCSV({
        startDate: formatDateForAPI(defaultDate),
        endDate: formatDateForAPI(defaultDate),
        filterParams:
          convertToSellsFilterParams(filterParams, dateAxis as SalesDateAxis) ||
          {},
        q: debouncedQuery ?? undefined,
      });
    }

    return SellsService.downloadExportSalesCSV({
      startDate: formatDateForAPI(fromDate),
      endDate: formatDateForAPI(toDate),
      filterParams:
        convertToSellsFilterParams(filterParams, dateAxis as SalesDateAxis) ||
        {},
      q: debouncedQuery ?? undefined,
    });
  }, [dateRange.from, dateRange.to, filterParams, debouncedQuery]);

  const handleExportError = useCallback(
    (err: unknown) => {
      if (isAxiosError(err)) {
        toast({ description: err?.message, variant: 'error' });
      } else {
        toast({
          description: t('common:data_listing_error'),
          variant: 'error',
        });
      }
    },
    [t],
  );

  const { mutate: exportFile, isPending: isExportingFile } = useMutation({
    mutationFn: handleExportFile,
    onSuccess: () => {
      toast({
        description: t('common:files_successfully_downloaded'),
        variant: 'success',
      });
    },
    onError: handleExportError,
  });

  const handleDateRangeChange = useCallback(
    (range: DateRange | undefined) => {
      setCurrentPage(1);
      if (range?.from) {
        setFromDate(range.from);
      }
      if (range?.to) {
        setToDate(range.to);
      }
    },
    [setCurrentPage, setFromDate, setToDate],
  );

  const handleDateAxisChange = useCallback(
    (axis: SalesDateAxis) => {
      setCurrentPage(1);
      setDateAxis(axis);

      setStatusFilter([...STATUSES_BY_DATE_AXIS[axis]]);
    },
    [setCurrentPage, setDateAxis, setStatusFilter],
  );

  const handleSearchChange = useCallback(
    (query: string) => {
      setCurrentPage(1);
      setSearchQuery(query || null);
    },
    [setCurrentPage, setSearchQuery],
  );

  const getSalesPeriodText = useCallback(
    (range: DateRange | undefined): string => {
      const locale = getLocale();
      if (!range?.from) return t('sales');

      if (range.to && isSameDay(range.from, range.to) && isToday(range.from)) {
        return t('paid');
      }

      if (range.to && isSameDay(range.from, range.to)) {
        return t('single_day', {
          date: format(range.from, 'dd/MM/yyyy', { locale }),
        });
      }

      if (range.to) {
        const days = differenceInDays(range.to, range.from);
        if (days === 6) return t('last_7_days');
        if (days === 29) return t('last_30_days');
        if (days === 89) return t('last_90_days');

        return t('date_range', {
          startDate: format(range.from, 'dd/MM', { locale }),
          endDate: format(range.to, 'dd/MM', { locale }),
        });
      }

      return t('sales');
    },
    [t],
  );

  const setFilterParams = useCallback(
    (params: FilterParams | ((prev: FilterParams) => FilterParams)) => {
      const newParams =
        typeof params === 'function' ? params(filterParams) : params;

      setStatusFilter((newParams.status as string[]) || null);
      setPaymentMethodsFilter((newParams.payment_methods as string[]) || null);
      setSrcFilter((newParams.src as string) || null);
      setUtmCampaignFilter((newParams.utm_campaign as string) || null);
      setUtmSourceFilter((newParams.utm_source as string) || null);
      setUtmMediumFilter((newParams.utm_medium as string) || null);
      setUtmContentFilter((newParams.utm_content as string) || null);
      setUtmTermFilter((newParams.utm_term as string) || null);
      setOffersFilter((newParams.offers as string[]) || null);
      setProductsFilter((newParams.products as string[]) || null);
      setAffiliatesFilter((newParams.affiliates as string[]) || null);
      setCoproducersFilter((newParams.coproducers as string[]) || null);
      setTemplatesFilter((newParams.templates as string[]) || null);
    },
    [
      filterParams,
      setStatusFilter,
      setPaymentMethodsFilter,
      setSrcFilter,
      setUtmCampaignFilter,
      setUtmSourceFilter,
      setUtmMediumFilter,
      setUtmContentFilter,
      setUtmTermFilter,
      setOffersFilter,
      setProductsFilter,
      setAffiliatesFilter,
      setCoproducersFilter,
      setTemplatesFilter,
    ],
  );

  const removeFilterItem = useCallback(
    (key: string, index: number) => {
      const filterSetters = {
        status: setStatusFilter,
        payment_methods: setPaymentMethodsFilter,
        offers: setOffersFilter,
        products: setProductsFilter,
        affiliates: setAffiliatesFilter,
        coproducers: setCoproducersFilter,
        templates: setTemplatesFilter,
      };

      const setter = filterSetters[key as keyof typeof filterSetters];
      if (setter) {
        const currentValues = filterParams[key] as string[];
        const newValues = currentValues?.filter((_, i) => i !== index);
        setter(newValues?.length ? newValues : null);
      }
    },
    [
      filterParams,
      setStatusFilter,
      setPaymentMethodsFilter,
      setOffersFilter,
      setProductsFilter,
      setAffiliatesFilter,
      setCoproducersFilter,
      setTemplatesFilter,
    ],
  );

  const removeFilter = useCallback(
    (key: string) => {
      const filterSetters = {
        status: setStatusFilter,
        payment_methods: setPaymentMethodsFilter,
        src: setSrcFilter,
        utm_campaign: setUtmCampaignFilter,
        utm_source: setUtmSourceFilter,
        utm_medium: setUtmMediumFilter,
        utm_content: setUtmContentFilter,
        utm_term: setUtmTermFilter,
        offers: setOffersFilter,
        products: setProductsFilter,
        affiliates: setAffiliatesFilter,
        coproducers: setCoproducersFilter,
        templates: setTemplatesFilter,
      };

      const setter = filterSetters[key as keyof typeof filterSetters];
      if (setter) {
        setter(null);
      }
    },
    [
      setStatusFilter,
      setPaymentMethodsFilter,
      setSrcFilter,
      setUtmCampaignFilter,
      setUtmSourceFilter,
      setUtmMediumFilter,
      setUtmContentFilter,
      setUtmTermFilter,
      setOffersFilter,
      setProductsFilter,
      setAffiliatesFilter,
      setCoproducersFilter,
      setTemplatesFilter,
    ],
  );

  const clearAllFilters = useCallback(() => {
    setStatusFilter(null);
    setPaymentMethodsFilter(null);
    setSrcFilter(null);
    setUtmCampaignFilter(null);
    setUtmSourceFilter(null);
    setUtmMediumFilter(null);
    setUtmContentFilter(null);
    setUtmTermFilter(null);
    setOffersFilter(null);
    setProductsFilter(null);
    setAffiliatesFilter(null);
    setCoproducersFilter(null);
    setTemplatesFilter(null);

    setSearchQuery(null);

    setCurrentPage(1);

    handleDateRangeChange({
      from: new Date(),
      to: new Date(),
    });

    setFilterParams({});
  }, [
    setStatusFilter,
    setPaymentMethodsFilter,
    setSrcFilter,
    setUtmCampaignFilter,
    setUtmSourceFilter,
    setUtmMediumFilter,
    setUtmContentFilter,
    setUtmTermFilter,
    setOffersFilter,
    setProductsFilter,
    setAffiliatesFilter,
    setCoproducersFilter,
    setTemplatesFilter,
    setSearchQuery,
    setCurrentPage,
    setFromDate,
    setToDate,
  ]);

  const filterBadgeLabel: FilterBadgeLabel = useMemo(
    () => ({
      paid: t('paid'),
      awaiting_payment: t('pending'),
      not_authorized: t('not_authorized'),
      chargeback: t('common:chargeback'),
      pre_chargeback: t('common:pre_chargeback'),
      pix: t('pix'),
      credit_card: t('credit_card'),
      bank_slip: t('bank_slip'),
      wallet_picpay: t('common:wallet_picpay'),
      wallet_google_pay: t('common:wallet_google_pay'),
      wallet_apple_pay: t('common:wallet_apple_pay'),
      paypal: t('common:paypal'),
      refunded: t('common:refunded'),
      '1': t('filterInvoiceApproved'),
      invoice_not_approved: t('filterInvoiceNotApproved'),
      venmo: t('common:venmo'),
      paze: t('common:paze'),
    }),
    [t],
  );

  const keyLabel: KeyLabel = useMemo(
    () => ({
      status: t('status'),
      payment_methods: t('payment_method'),
      src: t('src'),
      utm_campaign: t('utm_campaign'),
      utm_source: t('utm_source'),
      utm_medium: t('utm_medium'),
      utm_content: t('utm_content'),
      utm_term: t('utm_term'),
      offers: t('offer'),
      products: t('product'),
      affiliates: t('affiliate'),
      coproducers: t('co_producer'),
      templates: t('reports:template'),
    }),
    [t],
  );

  const hasActiveFilters = useMemo(
    () => hasActiveFiltersBeyondDates(filterParams, fromDate, toDate),
    [filterParams, fromDate, toDate],
  );

  const showPendingCard = useMemo(() => {
    const status = filterParams.status;
    if (!status || !Array.isArray(status)) return false;
    return status.includes('awaiting_payment');
  }, [filterParams.status]);

  const showRejectedCard = useMemo(() => {
    const status = filterParams.status;
    if (!status || !Array.isArray(status)) return false;
    return status.includes('not_authorized');
  }, [filterParams.status]);

  const showChargebackCard = useMemo(() => {
    const status = filterParams.status;
    if (!status || !Array.isArray(status)) return false;
    return status.includes('chargeback');
  }, [filterParams.status]);

  const showRefundedCard = useMemo(() => {
    const status = filterParams.status;
    if (!status || !Array.isArray(status)) return false;
    return status.includes('refunded');
  }, [filterParams.status]);

  return {
    reports: reportsAndPaidSalesData?.reports,
    paidSales: reportsAndPaidSalesData?.paidSales,
    sales: salesData,
    dateRange,
    dateAxis: (dateAxis as SalesDateAxis) || 'processing',

    isLoadingReports: isLoadingReportsAndPaidSales,
    isLoadingPaidSales: isLoadingReportsAndPaidSales,
    isLoadingSales,

    isFetchingReports: isFetchingReportsAndPaidSales,
    isFetchingPaidSales: isFetchingReportsAndPaidSales,
    isFetchingSales,

    reportsError,
    salesError,
    queryError: salesError || reportsError,

    handleDateRangeChange,
    handleSearchChange,
    getSalesPeriodText,
    handleDateAxisChange,

    currentPage,
    setCurrentPage,

    filterParams,
    setFilterParams,
    filterVisualParams,
    hasActiveFiltersBeyondDates: hasActiveFilters,
    removeFilterItem,
    removeFilter,
    clearAllFilters,

    filterBadgeLabel,
    keyLabel,

    exportFile,
    isExportingFile,

    searchQuery,
    currency,

    showPendingCard,
    showRejectedCard,
    showChargebackCard,
    showRefundedCard,
  };
};
